import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DollarSign, Timer, Trophy, TrendingUp, RefreshCw, Target } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => (n >= 1000 ? n.toLocaleString("es") : n);

function Bar({ label, cur, goal, pct }) {
    const color = pct >= 100 ? "#39FF14" : pct >= 40 ? "#FFD700" : "#FF4757";
    return (
        <div className="mb-3" data-testid={`bar-${label}`}>
            <div className="mb-1 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wider text-muted-foreground">{label}</span>
                <span className="font-mono" style={{ color }}>
                    {fmt(cur)} / {fmt(goal)} ({pct}%)
                </span>
            </div>
            <div className="h-2 w-full overflow-hidden bg-secondary">
                <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
}

function Milestone({ title, icon: Icon, data, orTip }) {
    return (
        <div className={`border p-4 ${data.unlocked ? "border-primary" : "border-border"}`}>
            <div className="mb-3 flex items-center gap-2">
                <Icon size={16} className={data.unlocked ? "text-primary" : "text-muted-foreground"} />
                <h3 className="font-head text-sm">{title}</h3>
                {data.unlocked && (
                    <span className="ml-auto bg-primary px-2 py-0.5 text-[9px] font-bold uppercase text-black">
                        Desbloqueado
                    </span>
                )}
            </div>
            {data.subs && <Bar label="Suscriptores" {...data.subs} />}
            {data.uploads && <Bar label="Subidas (90d)" {...data.uploads} />}
            <Bar label="Horas long-form (365d)" {...data.watch_hours} />
            <div className="my-1 text-center text-[9px] uppercase tracking-widest text-muted-foreground">— o —</div>
            <Bar label="Vistas shorts (90d)" {...data.shorts_views} />
            <p className="mt-1 text-[10px] text-muted-foreground">{orTip}</p>
        </div>
    );
}

export default function Monetization() {
    const qc = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ["monetization"],
        queryFn: async () => (await api.get("/monetization")).data,
        refetchInterval: 30000,
    });

    const sync = useMutation({
        mutationFn: async (cid) => (await api.post(`/channels/${cid}/sync`)).data,
        onSuccess: () => {
            toast.success("Sincronizado con YouTube Analytics");
            qc.invalidateQueries({ queryKey: ["monetization"] });
        },
        onError: () => toast.error("No se pudo sincronizar"),
    });

    if (isLoading) return <div className="text-sm text-muted-foreground">Cargando cabina…</div>;
    const c = data?.channels?.[0];

    return (
        <div className="space-y-6" data-testid="monetization-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 font-head text-2xl">
                        <DollarSign size={22} className="text-primary" /> Cabina de Monetización
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Experto en el Programa de Socios de YouTube (reglas 2026) — datos reales de tu canal.
                    </p>
                </div>
                <div className="flex items-center gap-2 border border-[#C81D25] bg-[#C81D25]/10 px-4 py-2" data-testid="ypp-countdown">
                    <Timer size={16} className="text-[#ff5964]" />
                    <div className="leading-none">
                        <div className="font-head text-xl text-[#ff5964]">{data?.days_to_deadline} días</div>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                            para el alza de requisitos (1 feb 2027)
                        </div>
                    </div>
                </div>
            </div>

            {!c && (
                <div className="border border-border p-6 text-sm text-muted-foreground">
                    Vincula un canal de YouTube en Integraciones para ver tu progreso de monetización.
                </div>
            )}

            {c && (
                <>
                    {data?.coach && (
                        <div className="border border-primary/40 bg-primary/5 p-5" data-testid="coach-card">
                            <div className="mb-2 flex items-center gap-2">
                                <Target size={16} className="text-primary" />
                                <h2 className="font-head text-sm">Coach IA de Monetización</h2>
                            </div>
                            <p className="text-sm">{data.coach.resumen}</p>
                            {data.coach.foco_semana && (
                                <div className="mt-3 border-l-2 border-primary pl-3 text-sm">
                                    <span className="text-[10px] uppercase tracking-widest text-primary">Foco de la semana</span>
                                    <p className="font-medium">{data.coach.foco_semana}</p>
                                </div>
                            )}
                            <ul className="mt-3 space-y-1.5">
                                {(data.coach.acciones || []).map((a, i) => (
                                    <li key={i} className="flex gap-2 text-sm">
                                        <span className="text-primary">▸</span>
                                        <span>{a}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="font-head text-sm">{c.name}</span>
                            <span className="text-xs text-muted-foreground">{c.handle}</span>
                        </div>
                        <button
                            data-testid="mon-sync-btn"
                            onClick={() => sync.mutate(c.channel_id)}
                            disabled={sync.isPending}
                            className="flex items-center gap-2 border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider transition-colors hover:border-primary hover:text-primary disabled:opacity-40"
                        >
                            <RefreshCw size={13} className={sync.isPending ? "animate-spin" : ""} />
                            Sincronizar
                        </button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="border border-border p-4">
                            <TrendingUp size={16} className="text-primary" />
                            <div className="mt-2 font-head text-3xl">{fmt(c.subscribers)}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Suscriptores</div>
                        </div>
                        <div className="border border-border p-4">
                            <Timer size={16} className="text-primary" />
                            <div className="mt-2 font-head text-3xl">{c.watch_hours_365}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Horas long-form (365d)</div>
                        </div>
                        <div className="border border-border p-4">
                            <TrendingUp size={16} className="text-primary" />
                            <div className="mt-2 font-head text-3xl">{fmt(c.shorts_views_90d)}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vistas shorts (90d)</div>
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <Milestone
                            title="Hito 1 · Acceso temprano (membresías)"
                            icon={Trophy}
                            data={c.early_access}
                            orTip="Necesitas las horas O las vistas de shorts (una de las dos)."
                        />
                        <Milestone
                            title="Hito 2 · YPP completo (anuncios)"
                            icon={DollarSign}
                            data={c.full_ypp}
                            orTip="El dinero real llega aquí. Long-form = RPM alto; shorts = RPM bajo."
                        />
                    </div>
                </>
            )}
        </div>
    );
}
